import { useMemo, useCallback } from 'react'

import _ from 'lodash'

import { getConfig } from '@/shared/config/journeys'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useBlocker } from '@/shared/hooks/useBlocker'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { sortByPosition } from '@/shared/lib/array/sort-by-position'
import { calcPercent } from '@/shared/lib/number/calc-percent'
import type { BlockCondition } from '@/types/blocker'
import type {
  Journey,
  Stage,
  Level,
  Challenge,
  StageWithUnderscores,
  LevelWithUnderscores
} from '@/types/journey'


import { filterVisibleForGuilds } from '@/shared/lib/guild/visibility'

import { useGamificationDataStore } from './gamificationDataStore'

const EMPTY_ARR: any[] = []

export const useJourneysStore = ({
  id,
  uid
}: {
  id?: number
  uid?: string
} = {}) => {
  const {
    challenges,
    levels: levelsRaw,
    stages: stagesRaw,
    journeys: journeysRaw,
    version
  } = useGamificationDataStore(s => s.getActiveProductData())

  const {
    userProgress,

    guildIds,
    journeyWasFinished,
    stageWasFinished,
    levelWasFinished,
    challengeWasFinished,
    challengeWasCorrect
  } = useUserProgress()

  const { generateConditions } = useBlocker()

  const { digitalProductId } = useDigitalProductContext()
  const config = getConfig({ digitalProductId })

  const stages = useMemo(() => {
    const sortedStages = stagesRaw.slice().sort(sortByPosition)

    return sortedStages.map(stage => {
      const _levels = levelsRaw
        .filter(l => l.stage_id === stage.id)
        .sort(sortByPosition)
      const _totalCoinReward = _.sumBy(_levels, 'total_coin_reward')
      const _totalPointReward = _.sumBy(_levels, 'total_point_reward')

      return {
        ...stage,
        _levels,
        _totalCoinReward,
        _totalPointReward
      }
    })
  }, [stagesRaw])

  const levels = useMemo(() => {
    const linearLevels: Level[] = []

    for (const stage of stages) {
      linearLevels.push(
        ...stage._levels.map(l => ({
          ...l,
          stage_id: stage.id,
          journey_id: stage.journey_id
        }))
      )
    }

    return linearLevels
  }, [stages, levelsRaw])

  /**
   * Get Stage Progress
   * @param stage - Stage
   * @param levelsProps - Levels
   * @returns Stage Progress
   */
  const getStageProgress = (
    { id, coin_reward, point_reward }: Stage,
    stageLevels: Level[]
  ) => {
    const finished = stageWasFinished(id)

    let allLevelsFinished = true
    let totalFinishedCount = 0
    let rewards = {
      acquiredCoins: 0,
      acquiredPoints: 0,
      totalCoins: coin_reward || 0,
      totalPoints: point_reward || 0
    }

    stageLevels.forEach(level => {
      if (levelWasFinished(level.id)) {
        rewards.acquiredCoins += level.total_coin_reward || 0
        rewards.acquiredPoints += level.total_point_reward || 0
        totalFinishedCount++
      } else allLevelsFinished = false
    })

    if (allLevelsFinished) {
      rewards.acquiredCoins += coin_reward || 0
      rewards.acquiredPoints += point_reward || 0
    }

    return {
      finished,
      totalCount: stageLevels.length,
      totalFinishedCount,
      finishedPercent: calcPercent(totalFinishedCount, stageLevels.length),
      rewards
    }
  }

  /**
   * Get Level Progress
   * @param level - Level
   * @param challengesProps - Challenges
   * @param previousLevel - Previous Level
   * @returns Level Progress
   */
  const getLevelProgress = (level: Level) => {
    const finished = levelWasFinished(level.id)

    let allChallengesFinished = true
    let rewards = {
      acquiredCoins: 0,
      acquiredPoints: 0,
      totalCoins: level?.coin_reward || 0,
      totalPoints: level?.point_reward || 0
    }
    let levelChallenges = challenges.filter(
      challenge => challenge.level_id === level.id
    )
    levelChallenges = levelChallenges.sort(sortByPosition)
    levelChallenges.forEach(challenge => {
      rewards.totalCoins += challenge?.coin_reward || 0
      rewards.totalPoints += challenge?.point_reward || 0

      if (challengeWasFinished(challenge.id)) {
        if (challengeWasCorrect(challenge.id)) {
          rewards.acquiredCoins += challenge?.coin_reward || 0
          rewards.acquiredPoints += challenge?.point_reward || 0
        }
      } else allChallengesFinished = false
    })

    if (allChallengesFinished) {
      rewards.acquiredCoins += level?.coin_reward || 0
      rewards.acquiredPoints += level?.point_reward || 0
    }

    return {
      finished,
      rewards
    }
  }

  const getPreviousLevel = (level: Level): Level | undefined => {
    let _levels = [...levels]
    const journeyId =
      level.journey_id ??
      level.stage?.journey_id ??
      stages.find(s => s.id === level.stage_id)?.journey_id
    _levels = levels.filter(l => l.journey_id === journeyId)

    const index = _levels.findIndex(l => l.id === level.id)
    return index > 0 ? _levels[index - 1] : undefined
  }

  /**
   * Get Journey Progress
   * @param journey - Journey
   * @returns Journey Progress
   */
  const getJourneyProgress = (journey: Journey) => {
    const finished = journeyWasFinished(journey.id)

    const allFinishedLevels = new Set(
      userProgress?.finished_levels ?? EMPTY_ARR
    )
    const activeLevels = journey.active_level_ids ?? EMPTY_ARR
    const finishedLevels = activeLevels.reduce(
      (acc, id) => acc + (allFinishedLevels.has(id) ? 1 : 0),
      0
    )
    const finishedPercent = calcPercent(finishedLevels, activeLevels.length)

    return {
      finished,
      finishedPercent
    }
  }

  /**
   * Journeys
   */
  const journeys = useMemo(() => {
    // O bundle traz as trilhas do evento inteiro; o recorte por guilda é
    // decidido aqui. A imposição de verdade é do servidor, na escrita.
    const visibleJourneys = filterVisibleForGuilds(journeysRaw as Journey[], guildIds)
    const sortedJourneys = visibleJourneys.slice().sort(sortByPosition)

    return sortedJourneys.map(journey => {
      const _stages = stages.filter(stage => stage.journey_id === journey.id)
      const _totalCoinReward =
        (journey.coin_reward ?? 0) + _.sumBy(_stages, '_totalCoinReward')
      const _totalPointReward =
        (journey.point_reward ?? 0) + _.sumBy(_stages, '_totalPointReward')

      let _blockCondition: BlockCondition = {
        type: 'and',
        conditions: generateConditions(journey)
      }

      return {
        ...journey,

        _blockCondition,

        _progress: getJourneyProgress(journey),
        _totalCoinReward,
        _totalPointReward
      }
    })
  }, [journeysRaw, guildIds, stages, levels])

  const baseJourney = useMemo(() => {
    if (!id && !uid) return null

    const journeyData = journeys.find(j => j.uid === uid || j.id === id)
    if (!journeyData) return null

    const _stages = stages
      .filter(stage => stage.journey_id === journeyData.id)
      .sort(sortByPosition)
      .map(stage => {
        const stageIsOpen = !!stage?.is_open

        const _levels = levels
          .filter(level => level.stage_id === stage.id)
          .map(level => {
            const _blockCondition: BlockCondition = {
              type: 'and',
              conditions: generateConditions(level)
            }

            if (!stageIsOpen || level?.unlock_after_previous_level) {
              const _previousLevel = getPreviousLevel(level)
              if (_previousLevel)
                _blockCondition.conditions.push({
                  type: 'resource',
                  resource: {
                    type: 'level',
                    id: _previousLevel.id,
                    uid: _previousLevel.uid,
                    name: _previousLevel.name
                  },
                  condition: 'finished'
                })
            }

            // Uma condição por nível exigido: a árvore já é um AND, então
            // empurrar N em vez de 1 é a generalização natural do mecanismo —
            // sem tipo novo e sem tocar o useBlocker. Só vale em etapa aberta.
            if (stageIsOpen && level?.unlock_after_levels?.length) {
              level.unlock_after_levels.forEach(requiredId => {
                const _required = levels.find(l => l.id === requiredId)
                if (!_required) return
                _blockCondition.conditions.push({
                  type: 'resource',
                  resource: {
                    type: 'level',
                    id: _required.id,
                    uid: _required.uid,
                    name: _required.name
                  },
                  condition: 'finished'
                })
              })
            }

            if (journeyData.block_redo) {
              _blockCondition.conditions.push({
                type: 'resource',
                resource: {
                  type: 'level',
                  id: level.id,
                  uid: level.uid,
                  name: level.name
                },
                condition: 'blockRedo'
              })
            }
            return { ...level, _blockCondition }
          })

        const _totalCoinReward = _.sumBy(_levels, 'total_coin_reward')
        const _totalPointReward = _.sumBy(_levels, 'total_point_reward')

        return { ...stage, _levels, _totalCoinReward, _totalPointReward }
      }) as StageWithUnderscores[]

    return { ...journeyData, _stages }
  }, [journeys, id, uid, levels, stages])

  const journey = useMemo(() => {
    if (!baseJourney) return null

    let _currentLevel: LevelWithUnderscores | null = null

    const _stages = baseJourney._stages.map(stage => {
      const _levels = stage._levels.map(level => {
        const _levelProgress = getLevelProgress(level)
        const _levelFinished = levelWasFinished(level.id)
        const _levelCurrent = !_currentLevel && !_levelFinished
        if (_levelCurrent) _currentLevel = level

        return {
          ...level,
          _finished: _levelFinished,
          _current: _levelCurrent,
          _progress: _levelProgress
        }
      })

      const _stageProgress = getStageProgress(stage, _levels)
      return { ...stage, _levels, _progress: _stageProgress }
    })

    const _progress = getJourneyProgress(baseJourney)

    return {
      ...baseJourney,
      _stages,
      _progress,
      _currentLevel
    }
  }, [baseJourney, userProgress])

  const getChallengeProgress = (challenge: Challenge) => {
    const finished = challengeWasFinished(challenge.id)
    const correctAnswer = challengeWasCorrect(challenge.id)

    return { finished, correctAnswer }
  }

  const getLevelDetails = useCallback(
    (level: Level) => {
      if (!level) return null

      const _challenges = (level?.active_challenges ?? [])
        .sort(sortByPosition)
        .map(challenge => {
          const _progress = getChallengeProgress(challenge)

          return {
            ...challenge,
            _progress
          }
        })
      const _lastUnfinishedChallenge = _challenges.find(
        c => !c._progress.finished
      )
      const _finishedChallenges = _challenges.filter(c => c._progress.finished)
      const _allChallengesFinished =
        _finishedChallenges.length === _challenges.length
      const _levelContinue =
        !_allChallengesFinished &&
        _finishedChallenges.some(c => c._progress.finished)
      const _finishedPercent = calcPercent(
        _finishedChallenges.length,
        _challenges.length
      )
      const _allChallengesCorrect = _challenges.filter(
        c => c._progress.correctAnswer
      )
      const _correctPercent = calcPercent(
        _allChallengesCorrect.length,
        _challenges.length
      )

      const _progress = getLevelProgress(level)
      let _blockCondition: BlockCondition = {
        type: 'and',
        conditions: generateConditions(level)
      }

      if (!level?.stage?.is_open || level?.unlock_after_previous_level) {
        const _previousLevel = getPreviousLevel(level)
        if (_previousLevel)
          _blockCondition.conditions.push({
            type: 'resource',
            resource: {
              type: 'level',
              id: _previousLevel.id,
              uid: _previousLevel.uid,
              name: _previousLevel.name
            },
            condition: 'finished'
          })
      }

      // Mesmo mecanismo do ponto acima; aqui o predicado de etapa é
      // `level?.stage?.is_open`, porque `stageIsOpen` não existe neste escopo.
      if (level?.stage?.is_open && level?.unlock_after_levels?.length) {
        level.unlock_after_levels.forEach(requiredId => {
          const _required = levels.find(l => l.id === requiredId)
          if (!_required) return
          _blockCondition.conditions.push({
            type: 'resource',
            resource: {
              type: 'level',
              id: _required.id,
              uid: _required.uid,
              name: _required.name
            },
            condition: 'finished'
          })
        })
      }

      if (level?.journey?.block_redo) {
        _blockCondition.conditions.push({
          type: 'resource',
          resource: {
            type: 'level',
            id: level.id,
            uid: level.uid,
            name: level.name
          },
          condition: 'blockRedo'
        })
      }

      return {
        ...level,

        _blockCondition,

        _progress,
        _challenges,
        _lastUnfinishedChallenge,
        _allChallengesFinished,
        _finishedPercent,
        _correctPercent,
        _levelContinue
      }
    },
    [
      challenges,
      generateConditions,
      getPreviousLevel,
      levelWasFinished,
      challengeWasFinished,
      challengeWasCorrect
    ]
  )

  return {
    journeys,
    journey,
    config,
    isDataLoaded: version !== '',

    getLevelDetails
  }
}
